import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { ActionButton } from '@/components/ui/ActionButton';
import { allowedEmailDomainsText } from '@/lib/emailDomains';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            ux_mode?: string;
            auto_select?: boolean;
          }) => void;
          renderButton: (
            el: HTMLElement,
            options: { theme?: string; size?: string; type?: string; width?: number },
          ) => void;
          prompt: () => void;
        };
      };
    };
  }
}

const GIS_SRC = 'https://accounts.google.com/gsi/client';
const CLIENT_ID =
  (import.meta as unknown as { env?: { VITE_GOOGLE_CLIENT_ID?: string } }).env
    ?.VITE_GOOGLE_CLIENT_ID ?? '';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refresh, loading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [gisReady, setGisReady] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);

  const next = searchParams.get('next') ?? '/';

  // Already logged in? Bounce to next.
  useEffect(() => {
    if (!loading && user) {
      navigate(next, { replace: true });
    }
  }, [user, loading, navigate, next]);

  const handleCredential = async (idToken: string) => {
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.post('/auth/google', { id_token: idToken });
      await refresh();
      navigate(next, { replace: true });
    } catch (e) {
      setError((e as Error).message ?? 'Sign-in failed');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!CLIENT_ID) {
      setError('Google client ID not configured (VITE_GOOGLE_CLIENT_ID).');
      return;
    }
    const existing = document.querySelector(`script[src="${GIS_SRC}"]`);
    const onLoad = () => {
      try {
        if (!window.google) throw new Error('GIS not available');
        window.google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: (resp) => {
            void handleCredential(resp.credential);
          },
        });
        if (buttonRef.current) {
          window.google.accounts.id.renderButton(buttonRef.current, {
            theme: 'outline',
            size: 'large',
            type: 'standard',
            width: 280,
          });
        }
        setGisReady(true);
      } catch (e) {
        setError((e as Error).message ?? 'Failed to initialise Google Sign-In');
      }
    };

    if (existing) {
      onLoad();
    } else {
      const s = document.createElement('script');
      s.src = GIS_SRC;
      s.async = true;
      s.defer = true;
      s.onload = onLoad;
      s.onerror = () => setError('Could not load Google Sign-In');
      document.head.appendChild(s);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="flex min-h-screen items-center justify-center bg-neutral-50 p-24">
      <section className="w-full max-w-md rounded-xl border border-neutral-200 bg-neutral-0 p-32 shadow-sm">
        <div className="flex items-center gap-12">
          <span className="flex h-40 w-40 items-center justify-center rounded-md bg-primary-500 text-lg font-semibold text-neutral-0">
            S
          </span>
          <div>
            <h1 className="text-2xl font-semibold text-neutral-900">Sign in</h1>
            <p className="text-sm text-neutral-500">Shahzadtex Approvals</p>
          </div>
        </div>
        <p className="mt-20 text-base text-neutral-600">
          Use your{' '}
          <span className="font-medium text-neutral-800">
            {allowedEmailDomainsText()}
          </span>{' '}
          Google account to continue.
        </p>
        <div className="mt-24 flex flex-col items-center gap-12">
          <div ref={buttonRef} aria-busy={submitting || undefined} />
          {!gisReady && !error && (
            <p className="text-sm text-neutral-500">Loading Google Sign-In...</p>
          )}
          {error && (
            <div className="w-full rounded-md border border-error-500/30 bg-error-500/10 p-12 text-sm text-error-500">
              {error}
            </div>
          )}
          {submitting && (
            <ActionButton variant="ghost" loading disabled>
              Signing in...
            </ActionButton>
          )}
        </div>
      </section>
    </main>
  );
}
